import { useMemo } from "react";

import { type Option } from "@/components/types/option";
import { includesLike } from "@/packages/orion/helpers";

import { Divider } from "..";

type FilterOptionsProps<TValue extends Option> = {
  optionLabel: string;
  query: string;
  options?: (TValue | Divider)[];
  filterCustom?: (opt: TValue, query: string) => boolean;
  loading?: boolean;
};

function useFilterOptions<TValue extends Option>({
  query,
  optionLabel,
  options,
  filterCustom,
  loading,
}: FilterOptionsProps<TValue>) {
  const filtered = useMemo(() => {
    const inputValue = query?.toLowerCase().trim();
    if (!inputValue || !options) {
      return options;
    }

    const defaultFilterFunction = (option: TValue, query: string) => {
      return includesLike(option?.[optionLabel], query);
    };

    const filterFunction = filterCustom ?? defaultFilterFunction;
    const filtered = _withoutDivider(options).filter((option) =>
      filterFunction(option, inputValue),
    );
    return filtered;
  }, [query, options, filterCustom, optionLabel]);

  const hasFilterApplied = useMemo(() => {
    if (!filtered || !options) {
      return false;
    }

    return _withoutDivider(filtered).length !== _withoutDivider(options).length;
  }, [filtered, options]);

  const isEmptyResult = !loading && filtered && filtered.length === 0;

  return {
    filtered,
    hasFilterApplied,
    isEmptyResult,
  };
}

export { useFilterOptions };

function _withoutDivider<TValue extends Option = Option>(options: (TValue | Divider)[]): TValue[] {
  return options.filter((option) => !(option instanceof Divider)) as TValue[];
}
